import { create } from "zustand";

/**
 * Detects that this tab is running a bundle its origin no longer serves.
 *
 * Why it exists: remote employees keep the attendance page open all day, and a
 * deploy that changes an API contract turns every open tab into a client that
 * speaks the wrong language — the /attendance/mark changeover surfaced exactly
 * this, as a validation error nobody could act on. The check is self-origin on
 * purpose: the same commit is built separately for the two deployments (the
 * committed hrms-backend/public/ copy and the standalone frontend service), so
 * comparing against anything served by the *API* would flag one of them as
 * stale forever. Each origin serves the version.json of exactly the bundle it
 * serves, and that is the only pair that has to agree.
 *
 * Staleness is one-way: once seen, the only exit is a reload, so `stale` never
 * resets to false.
 */
interface StaleVersionState {
  stale: boolean;
  markStale: () => void;
}

export const useStaleVersion = create<StaleVersionState>((set) => ({
  stale: false,
  markStale: () => set({ stale: true }),
}));

/** Re-checks any more often than this are dropped (focus events can burst). */
const CHECK_COOLDOWN_MS = 60_000;
let lastCheckedAt = 0;

export async function checkForNewVersion(): Promise<void> {
  // The dev server serves no version.json, and hot reload makes the question
  // meaningless there anyway.
  if (import.meta.env.DEV) return;
  if (useStaleVersion.getState().stale) return;

  const now = Date.now();
  if (now - lastCheckedAt < CHECK_COOLDOWN_MS) return;
  lastCheckedAt = now;

  try {
    // Same-origin on purpose — see the module comment. The query param defeats
    // any intermediary cache that ignores no-store.
    const response = await fetch(`/version.json?t=${now}`, { cache: "no-store" });
    if (!response.ok) return;
    const body = (await response.json()) as { buildId?: string };
    if (body.buildId && body.buildId !== __BUILD_ID__) {
      useStaleVersion.getState().markStale();
    }
  } catch {
    // Offline, or an origin deployed before version.json existed (its SPA
    // fallback answers with HTML and the parse throws). Unknown is not stale.
  }
}
